import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CHARACTERS_ROOT } from '@/data/entities/characters';
import { fountainScenesEntity } from '@/data/entities/fountainScenes';
import { fragmentsEntity } from '@/data/entities/fragments';
import { scenesEntity } from '@/data/entities/scenes';
import { useVersion } from '@/hooks';
import { cancelSceneTagQueries } from '@/hooks/optimisticHelpers';
import type {
    ActiveFiltersType,
    CreateScene,
    IEntityTag,
    IFountainFragment,
    IFountainScene,
    IScenePresentationDuration,
    UpdateScene,
} from '@/types';
import { getFlatFilters } from '@/utils/helpers';
import { api } from './internal/endpoints';
import { applyTagOptimisticUpdate, rollbackTagOptimisticUpdate } from './internal/tagOptimisticUpdate';

export const useCreateScene = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            projectId,
            data,
        }: {
            projectId: number;
            data: CreateScene;
            version?: string;
            activeFilters?: Partial<ActiveFiltersType>;
        }) => api.createScene(projectId, data),
        onMutate: async variables => {
            const queryArgs = {
                project_id: variables.projectId,
                episode_id: variables.data.episode_id,
                version: variables.version,
                tags: `[${getFlatFilters(variables.activeFilters || {}).join(',')}]`,
            };

            await queryClient.cancelQueries({
                queryKey: scenesEntity.fetchers.presentations.key(queryArgs),
            });

            const previousScenes = queryClient.getQueryData(scenesEntity.fetchers.presentations.key(queryArgs));

            queryClient.setQueryData(
                scenesEntity.fetchers.presentations.key(queryArgs),
                (old: IScenePresentationDuration[] | undefined) => {
                    if (!old) return old;

                    const optimisticScene: IScenePresentationDuration = {
                        id: Date.now(),
                        code: '',
                        title: '',
                        updated_at: new Date().toISOString(),
                        number: old.length + 1,
                        position: variables.data.position || 1000,
                        act: variables.data.act || 1,
                        location: null,
                        tags: [],
                        text_atoms: [],
                        duration: 0,
                        sequences: [],
                    };

                    const newScenes = [...old];
                    const insertIndex = newScenes.findIndex(scene => scene.position > (variables.data.position || 1000));
                    if (insertIndex === -1) {
                        newScenes.push(optimisticScene);
                    } else {
                        newScenes.splice(insertIndex, 0, optimisticScene);
                    }

                    return newScenes;
                },
            );

            return { previousScenes, queryArgs };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousScenes && context?.queryArgs) {
                queryClient.setQueryData(scenesEntity.fetchers.presentations.key(context.queryArgs), context.previousScenes);
            }
        },
        onSuccess: (result, variables, context) => {
            if (result.success && context?.queryArgs) {
                queryClient.setQueryData(
                    scenesEntity.fetchers.presentations.key(context.queryArgs),
                    (old: IScenePresentationDuration[] | undefined) => {
                        if (!old) return old;

                        return old.map(scene => {
                            if (
                                scene.position === (variables.data.position || 1000) &&
                                scene.act === (variables.data.act || 1) &&
                                scene.id > 1000000000000
                            ) {
                                return {
                                    ...scene,
                                    id: result.data.id,
                                    number: result.data.number || scene.number,
                                };
                            }
                            return scene;
                        });
                    },
                );
            }
        },
        onSettled: () => {
            setTimeout(() => {
                Promise.all([
                    queryClient.refetchQueries({
                        queryKey: ['scenes'],
                        type: 'active',
                    }),
                    queryClient.refetchQueries({
                        queryKey: ['episodes'],
                        type: 'active',
                    }),
                ]);
            }, 1000);
        },
    });
};

export const useUpdateScene = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            projectId,
            sceneId,
            data,
        }: {
            projectId: number;
            sceneId: number;
            data: Partial<UpdateScene>;
            version?: string;
            characterInfo?: IEntityTag;
            tagInfo?: IEntityTag[];
        }) => api.updateScene(projectId, sceneId, data),
        onMutate: async ({ projectId, sceneId, data, version, characterInfo, tagInfo }) => {
            if (data.add_tags || data.remove_tags) {
                // Stop any in-flight scenes refetch before writing the optimistic
                // state, so its (pre-mutation) response can't land afterwards and
                // momentarily revert the removed/added chip.
                await cancelSceneTagQueries(queryClient);
                const previousData = applyTagOptimisticUpdate({
                    queryClient,
                    sceneId,
                    projectId,
                    version,
                    data,
                    characterInfo,
                    tagInfo,
                });
                return { previousData };
            }
            return undefined;
        },
        onError: (_err, _variables, context) => {
            if (context?.previousData) {
                rollbackTagOptimisticUpdate(queryClient, context.previousData);
            }
        },
        onSuccess: (result, variables) => {
            if (result.success) {
                const isOnlyTagOperation = !!(
                    (variables.data.add_tags || variables.data.remove_tags) &&
                    !variables.data.title &&
                    !variables.data.text_atoms &&
                    !variables.data.act &&
                    !variables.data.position &&
                    !variables.data.sequence_id &&
                    !variables.data.episode_id
                );

                if (isOnlyTagOperation) {
                    queryClient.invalidateQueries({
                        queryKey: fountainScenesEntity.fetchers.list.key({ projectId: variables.projectId }),
                    });
                    // #1815: attaching/detaching a character tag changes that
                    // character's scenes_count (which gates full-delete). Without
                    // this, removing a character from a scene leaves scenes_count
                    // stale and "can't delete while in a scene" persists until a
                    // page refresh. Mirrors the registry scene.updateTags row.
                    queryClient.invalidateQueries({ queryKey: CHARACTERS_ROOT });
                    return;
                }

                const shouldInvalidateTextAtoms = !!(
                    variables.data.title ||
                    variables.data.text_atoms ||
                    variables.data.act ||
                    variables.data.position ||
                    variables.data.sequence_id ||
                    variables.data.episode_id
                );

                queryClient.invalidateQueries({
                    predicate: query => {
                        const key = query.queryKey;
                        if (!Array.isArray(key) || key[0] !== 'scenes') {
                            return false;
                        }
                        if (key[1] === 'presentations') return true;
                        if (key[1] === 'textAtoms') return shouldInvalidateTextAtoms;
                        return false;
                    },
                });
            }
        },
    });
};

export const useDeleteScene = () => {
    const queryClient = useQueryClient();
    const version = useVersion();

    return useMutation({
        mutationFn: ({ projectId, sceneId }: { projectId: number; sceneId: number }) => api.deleteScene(projectId, sceneId),
        onMutate: async ({ projectId, sceneId }) => {
            // Pause refetches that would clobber our optimistic state.
            const fountainScenesKey = fountainScenesEntity.fetchers.list.key({ projectId, version });
            const fountainFragmentsKey = fragmentsEntity.fetchers.list.key({ projectId, version });
            await Promise.all([
                queryClient.cancelQueries({ queryKey: fountainScenesKey }),
                queryClient.cancelQueries({ queryKey: fountainFragmentsKey }),
            ]);

            const previousScenes = queryClient.getQueryData<IFountainScene[]>(fountainScenesKey);
            const previousFragments = queryClient.getQueryData<IFountainFragment[]>(fountainFragmentsKey);

            // Look up the scene's fragment_id from the cache so we can decrement
            // scenes_qty on the parent fragment without a network round-trip.
            const deletedScene = previousScenes?.find(s => s.id === sceneId);
            const fragmentId = deletedScene?.fragment_id;

            const fragmentCode = previousFragments?.find(f => f.id === fragmentId)?.code;

            // Optimistically remove the scene AND renumber the surviving siblings
            // in the same fragment (#1815). Mirrors the backend renumber-on-delete
            // (`code = fragment.code + '-' + rank`, 1-based by position order) and
            // the create path's updateAdditionalCaches. Without this the survivors
            // keep their pre-delete number/code until the settle-refetch, so the
            // editor's numbering looks stale until a page refresh.
            queryClient.setQueryData<IFountainScene[]>(fountainScenesKey, old => {
                if (!old) return old;
                const remaining = old.filter(s => s.id !== sceneId);
                if (fragmentId === undefined || !fragmentCode) return remaining;
                const rankById = new Map<number, number>();
                remaining
                    .filter(s => s.fragment_id === fragmentId)
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .forEach((s, idx) => {
                        rankById.set(s.id, idx + 1);
                    });
                return remaining.map(s => {
                    const rank = rankById.get(s.id);
                    return rank === undefined ? s : { ...s, number: rank, code: `${fragmentCode}-${rank}` };
                });
            });

            // Optimistically decrement scenes_qty on the parent fragment.
            if (fragmentId !== undefined) {
                queryClient.setQueryData<IFountainFragment[]>(fountainFragmentsKey, old => {
                    if (!old) return old;
                    return old.map(f => (f.id === fragmentId ? { ...f, scenes_qty: Math.max(0, (f.scenes_qty ?? 0) - 1) } : f));
                });
            }

            return { projectId, previousScenes, previousFragments };
        },
        onError: (_err, _variables, context) => {
            // Rollback both caches on error.
            if (!context) return;
            const fountainScenesKey = fountainScenesEntity.fetchers.list.key({ projectId: context.projectId, version });
            const fountainFragmentsKey = fragmentsEntity.fetchers.list.key({ projectId: context.projectId, version });
            if (context.previousScenes !== undefined) {
                queryClient.setQueryData(fountainScenesKey, context.previousScenes);
            }
            if (context.previousFragments !== undefined) {
                queryClient.setQueryData(fountainFragmentsKey, context.previousFragments);
            }
        },
        onSettled: (_result, _error, _variables) => {
            // Always refetch to reconcile any backend-derived fields (positions/numbers
            // get re-issued when their neighbors shift) and refresh non-fountain caches
            // (TV scenes, episode presentations) that we don't optimistically touch.
            queryClient.invalidateQueries({ queryKey: ['scenes'] });
            queryClient.invalidateQueries({ queryKey: ['fountain', 'scenes'] });
            queryClient.invalidateQueries({ queryKey: ['fountain', 'fragments'] });
        },
    });
};
